import { useSnackbar } from 'notistack';
import { Dispatch, MutableRefObject, useEffect, useReducer, useRef } from 'react';

export type Args = unknown[];
export type SourceFn<T, R extends Args> = (...args: R) => Promise<T>;
type TriggerFn<R extends Args> = (...args: R) => void;
type AbortFn = () => void;
type CreateTaskArgs<T, R extends Args> = {
  fn: SourceFn<T, R>;
  dispatchRef: MutableRefObject<Dispatch<Action<T>>>;
};

export interface Task<T, R extends Args> {
  taskId: symbol;
  runId: Nullable<symbol>;
  state: AsyncState;
  start: TriggerFn<R>;
  abort: AbortFn;
  result: T | undefined;
  error: any | undefined;
}

type Action<T> =
  | { type: 'ASYNC_START'; taskId: symbol; runId: Nullable<symbol> }
  | { type: 'ASYNC_CANCEL'; taskId: symbol; runId: Nullable<symbol> }
  | { type: 'ASYNC_DONE'; taskId: symbol; runId: Nullable<symbol>; payload: T }
  | { type: 'ASYNC_ERROR'; taskId: symbol; runId: Nullable<symbol>; payload: any };

export enum AsyncState {
  Idle = 'idle',
  InProgress = 'in-progress',
  Finished = 'finished',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

const createTask = <T, R extends Args>({ fn, dispatchRef }: CreateTaskArgs<T, R>): Task<T, R> => {
  const taskId = Symbol('TASK_ID');
  let abortController: Nullable<AbortController> = null;

  const start = async (...args: R) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const runId = Symbol('RUN_ID');

    try {
      let aborted = false;

      const handleAbort = () => {
        dispatchRef.current({ type: 'ASYNC_CANCEL', taskId, runId });
        aborted = true;
      };

      dispatchRef.current({ type: 'ASYNC_START', taskId, runId });

      abortController.signal.addEventListener('abort', handleAbort);

      const result = await fn(...args);

      abortController.signal.removeEventListener('abort', handleAbort);

      if (!aborted) {
        dispatchRef.current({ type: 'ASYNC_DONE', taskId, runId, payload: result });
      }
    } catch (error) {
      dispatchRef.current({ type: 'ASYNC_ERROR', taskId, runId, payload: error });
    }
  };

  const abort = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return {
    taskId,
    runId: null,
    start,
    abort,
    result: undefined,
    state: AsyncState.Idle,
    error: undefined,
  };
};

const reducer = <T, R extends Args>(task: Task<T, R>, action: Action<T>): Task<T, R> => {
  switch (action.type) {
    case 'ASYNC_START':
      if (task.taskId !== action.taskId) {
        return task;
      }
      return {
        ...task,
        runId: action.runId,
        state: AsyncState.InProgress,
        result: undefined,
        error: undefined,
      };
    case 'ASYNC_CANCEL':
      if (task.taskId !== action.taskId || task.runId !== action.runId) {
        return task;
      }
      return { ...task, state: AsyncState.Cancelled, result: undefined, error: undefined };
    case 'ASYNC_DONE':
      if (task.taskId !== action.taskId || task.runId !== action.runId) {
        return task;
      }
      return { ...task, state: AsyncState.Finished, result: action.payload, error: undefined };
    case 'ASYNC_ERROR':
      if (task.taskId !== action.taskId || task.runId !== action.runId) {
        return task;
      }
      return { ...task, state: AsyncState.Failed, result: undefined, error: action.payload };
    default:
      throw new Error(`Unknown action type`);
  }
};

/**
 *  Example:
 *    const { start, abort, state, result, error } = useAsyncV2(longRunningAsync);
 */
export const useAsync = <T, R extends Args>(fn: SourceFn<T, R>): Task<T, R> => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatchRef = useRef<Dispatch<Action<T>>>(() => {
    throw new Error('Not yetinitialized');
  });

  const [task, dispatch] = useReducer(reducer, { fn, dispatchRef } as any, createTask);
  dispatchRef.current = dispatch;

  useEffect(() => {
    return () => {
      dispatchRef.current = () => null;
    };
  }, []);

  useEffect(() => {
    if (task.error) {
      enqueueSnackbar(task.error.message || 'Unknown error', { variant: 'error' });
      console.error(task.error);
    }
  }, [task.error]);

  return task as Task<T, R>;
};
